import { css } from 'aphrodite';
import * as React from 'react';
import { useRouteMatch } from 'react-router';
import { BrokerDealer, Industry, TemplateCategoryType } from '../../../../../extViewmodels';
import { TemplateCategoryViewModel } from '../../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../../web/components-v2/Button';
import { Checkbox } from '../../../../../web/components-v2/Checkbox';
import { Input } from '../../../../../web/components-v2/Input';
import { Modal } from '../../../../../web/components/Modal';
import { ISelectBoxOption, SelectBox } from '../../../../../web/components/SelectBox';
import { bs } from '../../../../../web/styles/styles';
import { getBrokerDealerOptions } from '../../../../containers/account/getBrokerDealerOptions';
import { styleSheet } from '../styles';

interface Props {
	/** When provided, the modal operates in edit mode. */
	category?: TemplateCategoryViewModel;
	isOpen: boolean;
	onClose: () => void;
	onSaveClick: (categoryName: string, type: TemplateCategoryType, originalName?: string) => void;
}

export function CampaignLibraryNewCategoryModal({ category, isOpen, onClose, onSaveClick }: Props) {
	const isEditMode = !!category;
	const [value, setValue] = React.useState('');
	const [originalName, setOriginalName] = React.useState('');
	const [isBrokerDealer, setIsBrokerDealer] = React.useState<boolean>(false);
	const [selectedBrokerDealer, setSelectedBrokerDealer] = React.useState<BrokerDealer | null>(null);
	const match = useRouteMatch<{ category: string }>('/campaigns/:category');

	const industryFromUrl = React.useMemo(() => {
		const urlCategory = match.params.category || '';
		const matchedIndustry = Object.values(Industry).find(ind => ind.toLowerCase() === urlCategory.toLowerCase());
		return matchedIndustry;
	}, [match.params]);

	const [industry] = React.useState<Industry>(industryFromUrl);
	const brokerDealerOptions = React.useMemo(() => getBrokerDealerOptions(industry), [industry]);
	const hasBrokerDealers = !!brokerDealerOptions;

	React.useEffect(() => {
		if (isOpen && category) {
			setValue(category.name);
			setOriginalName(category.name);
			const categoryIsBD = category.type === TemplateCategoryType.BrokerDealer;
			setIsBrokerDealer(categoryIsBD);
			setSelectedBrokerDealer(categoryIsBD ? (category.name as BrokerDealer) : null);
		}
	}, [category, isOpen]);

	const onBrokerDealerChanged = (option: ISelectBoxOption<BrokerDealer>) => {
		setSelectedBrokerDealer(option.value);
		setValue(option.value);
	};

	const getBrokerDealerSelection = (): ISelectBoxOption<BrokerDealer> | null => {
		if (!selectedBrokerDealer) {
			return null;
		}
		return {
			title: selectedBrokerDealer,
			value: selectedBrokerDealer,
		};
	};

	const resetState = () => {
		setValue('');
		setOriginalName('');
		setIsBrokerDealer(false);
		setSelectedBrokerDealer(null);
	};

	const handleClose = () => {
		resetState();
		onClose();
	};

	const isValid = isBrokerDealer ? !!selectedBrokerDealer : value.trim().length > 0;

	const handleSave = () => {
		if (!isValid) {
			return;
		}
		const categoryType = isBrokerDealer ? TemplateCategoryType.BrokerDealer : TemplateCategoryType.Default;
		onSaveClick(value, categoryType, isEditMode ? originalName : undefined);
		resetState();
	};

	return (
		<Modal isOpen={isOpen} onRequestClose={handleClose} useDefaultHeader={true}>
			<div className={css(bs.textBrandSecondary, bs.textLg)}>{isEditMode ? 'Edit Category' : 'Add New Category'}</div>
			<div className={css(bs.textTitles, bs.textSm, bs.mt2, bs.mb1)}>Category Name</div>
			<Input onChange={ev => setValue(ev.target.value)} type='text' value={value} disabled={isBrokerDealer} />

			{hasBrokerDealers && (
				<div className={css(bs.mt3)}>
					<label className={css(styleSheet.checkboxLabel)}>
						<Checkbox checked={isBrokerDealer} onCheckedChange={checked => setIsBrokerDealer(checked === true)} />
						<span style={{ marginLeft: 8 }}>Broker Dealer Category</span>
					</label>
				</div>
			)}

			{hasBrokerDealers && isBrokerDealer && (
				<div className={css(bs.mt3)}>
					<div className={css(bs.textTitles, bs.textSm, bs.mb1)}>Broker Dealer</div>
					<SelectBox
						contentClassName={`${css(styleSheet.dropdownMenu)}`}
						onSelectionChanged={onBrokerDealerChanged}
						options={brokerDealerOptions || []}
						selectedOption={getBrokerDealerSelection()}
						triggerClassName={`${css(styleSheet.dropdownTrigger)}`}
					/>
				</div>
			)}

			<div className={css(bs.mt5, bs.flex, bs.itemsCenter, bs.gap5)}>
				<Button onClick={handleSave} disabled={!isValid}>
					<span>{isEditMode ? 'Save' : 'Create'}</span>
				</Button>
				<Button variant='secondary' onClick={handleClose}>
					<span>Cancel</span>
				</Button>
			</div>
		</Modal>
	);
}
